// toolArgs.js — normalize model tool-call arguments before IPC (DeepSeek/OpenRouter
// often send empty {}, wrong keys, or JSON strings).

export function parseToolArgs(raw) {
  if (raw == null) return {};
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return {};
    try {
      const parsed = JSON.parse(s);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      // Some models emit plain text args (e.g. "npm test") instead of JSON.
      // Keep the raw payload so name-aware repair can recover it later.
      return { __raw: s };
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...raw };
  return {};
}

function decodeHtmlEntities(text) {
  return String(text || '')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'");
}

function cleanCommandText(text) {
  let s = decodeHtmlEntities(text).trim();
  if (!s) return '';
  s = s.replace(/^```(?:bash|sh|shell)?\s*/i, '').replace(/```$/i, '').trim();
  s = s.replace(/^(rodar\s+comando|executar\s+comando|run\s+command)\s*:\s*/i, '').trim();
  return s;
}

function pickFirstString(...values) {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

export function extractRunCommand(args = {}) {
  const parsed = args && typeof args === 'object' ? args : {};

  const nestedArgs = parsed.args && typeof parsed.args === 'object' ? parsed.args : {};
  const nestedPayload = parsed.payload && typeof parsed.payload === 'object' ? parsed.payload : {};

  const direct = pickFirstString(
    parsed.command,
    parsed.cmd,
    parsed.shell,
    parsed.script,
    parsed.input,
    parsed.text,
    parsed.value,
    nestedArgs.command,
    nestedArgs.cmd,
    nestedPayload.command,
    nestedPayload.cmd,
  );
  if (direct) return cleanCommandText(direct);

  const fnArgsRaw = typeof parsed.function?.arguments === 'string' ? parsed.function.arguments.trim() : '';
  if (fnArgsRaw) {
    const fnParsed = parseToolArgs(fnArgsRaw);
    const fromFn = pickFirstString(fnParsed.command, fnParsed.cmd, fnParsed.shell, fnParsed.__raw);
    if (fromFn) return cleanCommandText(fromFn);
  }

  const raw = pickFirstString(parsed.__raw, parsed.raw);
  if (!raw) return '';
  const maybeJson = parseToolArgs(raw);
  const fromJson = pickFirstString(maybeJson.command, maybeJson.cmd, maybeJson.shell);
  if (fromJson) return cleanCommandText(fromJson);
  return cleanCommandText(raw);
}

/** Strip absolute paths to worktree-relative; fix apolo/apolo/ when root ends in /apolo. */
export function normalizeProjectPath(root, filePath) {
  let p = typeof filePath === 'string' ? filePath.trim() : '';
  if (!p) return '';
  if (root && p.startsWith('/')) {
    const cleanRoot = String(root).replace(/\/+$/, '');
    if (p === cleanRoot) return '';
    if (p.startsWith(`${cleanRoot}/`)) p = p.slice(cleanRoot.length + 1);
  }
  const base = String(root || '').replace(/\/+$/, '');
  if (base.endsWith('/apolo') && p.startsWith('apolo/')) {
    p = p.slice('apolo/'.length);
  }
  return p.replace(/^\/+/, '');
}

export function extractGrepQuery(args = {}) {
  const raw = args.pattern ?? args.query ?? args.q ?? args.search ?? args.text ?? args.term ?? '';
  const query = typeof raw === 'string' ? raw.trim() : String(raw || '').trim();
  return query;
}

export function extractFilePath(args = {}, root = '') {
  return normalizeProjectPath(root, args.path ?? args.filePath ?? args.file ?? args.filename ?? '');
}

export function extractListDir(args = {}, root) {
  return normalizeProjectPath(root, args.path ?? args.dir ?? args.directory ?? args.folder ?? '');
}

// FASE-12 E3-T04 — inferência de args REPO-AGNÓSTICA. Antes estas funções
// chutavam identificadores/paths do Apolo (ExecutionChat, ConversationFeed,
// apolo/src/components/code/chat). Num repo qualquer isso mandava o modelo grepar
// algo inexistente e queimava a iteração. Agora só extraem candidatos da PRÓPRIA
// task: nome de arquivo citado, identificador CamelCase, ou path/trecho literal.

/** Infer grep pattern when the model sends empty args (common DeepSeek/OpenRouter failure). */
export function inferGrepPatternFromTask(taskText) {
  const t = String(taskText || '');
  // 1) nome de arquivo citado → o basename sem extensão é um ótimo pattern.
  const file = t.match(/\b([A-Za-z][\w-]+)\.(?:jsx|tsx|css|js|ts|mjs|py|go|rs|java|rb|php|html?)\b/);
  if (file) return file[1];
  // 2) identificador CamelCase/PascalCase (componente, função, classe).
  const ident = t.match(/\b([A-Z][a-z0-9]+[A-Z][A-Za-z0-9]+)\b/);
  if (ident) return ident[1];
  // 3) trecho entre aspas (string literal do código a mexer).
  const quoted = t.match(/["'`]([^"'`]{2,48})["'`]/);
  if (quoted) return quoted[1];
  return '';
}

/** Infer list_files path when the model omits path (avoids empty-root listing). */
export function inferListPathFromTask(taskText) {
  const t = String(taskText || '');
  // Path de diretório citado literalmente (relativo, sem hardcode de repo).
  const dir = t.match(/\b((?:[\w-]+\/){1,}[\w-]+)\b/);
  if (dir && !/\.\w+$/.test(dir[1])) return dir[1].replace(/\/$/, '');
  // Fallback universal: convenção de raiz que quase todo projeto tem.
  if (/\bsrc\b/.test(t)) return 'src';
  return '';
}

/** Infer read_file path when the model omits it — só de um arquivo citado na task. */
export function inferReadPathFromTask(taskText) {
  const t = String(taskText || '');
  const explicit = t.match(/\b([\w/.-]+\.(?:jsx|tsx|css|js|ts|mjs|py|go|rs|java|rb|php|html?|md|json|ya?ml))\b/);
  return explicit ? explicit[1] : '';
}

/**
 * Repair malformed tool args using the user task as hint.
 * Returns a new args object (never mutates input).
 */
export function repairToolCall(name, args, { taskText = '', root = '' } = {}) {
  const parsed = parseToolArgs(args);
  switch (name) {
    case 'run_command': {
      const command = extractRunCommand(parsed);
      return command ? { ...parsed, command } : parsed;
    }
    case 'grep_search': {
      if (extractGrepQuery(parsed)) return parsed;
      const pattern = inferGrepPatternFromTask(taskText);
      return pattern ? { ...parsed, pattern } : parsed;
    }
    case 'list_files': {
      if (extractListDir(parsed, root)) return parsed;
      const path = inferListPathFromTask(taskText);
      return path ? { ...parsed, path } : parsed;
    }
    case 'read_file': {
      if (extractFilePath(parsed, root)) return parsed;
      const path = inferReadPathFromTask(taskText);
      return path ? { ...parsed, path } : parsed;
    }
    default:
      return parsed;
  }
}

export function toolArgsNeedRepair(name, args, { taskText = '', root = '' } = {}) {
  const parsed = parseToolArgs(args);
  const repaired = repairToolCall(name, args, { taskText, root });
  return JSON.stringify(parsed) !== JSON.stringify(repaired);
}
